/**
 * Web Storage adapter for the SiteMapViewStore port.
 *
 * Uses sessionStorage: it is per tab and survives reloads, like the browser history it is
 * keyed by, and is cleared when the tab closes. Storage can be missing, blocked or full;
 * then view state is simply not restored and nothing throws.
 */
import type { SiteMapViewStore } from '../models/siteMapModels';
import { findViewState, parseViewStateHistory, withViewState, type ViewStateHistory } from '../utils/viewStateHistory';

export const VIEW_STATE_STORAGE_KEY = 'siteMapViewState';

/** The part of the Web Storage API the adapter needs */
export type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

function readHistory(storage: KeyValueStorage): ViewStateHistory {
  try {
    const stored = storage.getItem(VIEW_STATE_STORAGE_KEY);
    return stored ? parseViewStateHistory(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
}

function writeHistory(storage: KeyValueStorage, history: ViewStateHistory): void {
  try {
    storage.setItem(VIEW_STATE_STORAGE_KEY, JSON.stringify(history));
  } catch {
    // Full or blocked storage only means the view is not restored
  }
}

export function createViewStore(storage: KeyValueStorage | null): SiteMapViewStore {
  if (!storage) {
    return { load: () => null, update: () => undefined };
  }
  return {
    load: (historyKey) => findViewState(readHistory(storage), historyKey),
    update: (historyKey, patch) => writeHistory(storage, withViewState(readHistory(storage), historyKey, patch)),
  };
}

/** Accessing `window.sessionStorage` itself throws when storage is blocked */
function browserSessionStorage(): KeyValueStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export const sessionViewStore: SiteMapViewStore = createViewStore(browserSessionStorage());
